// useNamesLinkOpen — the wire from the Names address to the Names department (ORD-NAMES-1, CLB-096).
//
// `/?tab=orders&names=1` opens the Names department inside Orders, but nothing read `?tab=`, so a
// cold link landed on the portal chooser. This opens the Orders portal once, after sign-in and
// after permissions have landed; Orders then reads `names=1` (names/useNamesRoute.js) and shows
// Names.
//
// Same shape as useCollectionLinkOpen: the address is captured at MODULE LOAD, because a reader
// who is signed out signs in first and only then does the shell mount. App.js is on the LOC
// grandfather list (Conv #163), so this is consumed from AppOverlays.
//
// Fail-closed: only a reader with Orders access is taken there; anyone else keeps the chooser.

import { useEffect } from 'react';
import { useUiStore } from '../stores/uiStore';
import { usePortalPermissions, canAccessPortal } from './usePortalPermissions';
import { openNamesAddress } from '../portals/orders/names/namesAddress';

const WANTED = (() => {
  try { const q = new URLSearchParams(window.location.search); return q.get('tab') === 'orders' && q.get('names') === '1'; } catch (_) { return false; }
})();
// once per page load, not per mount: AppOverlays remounts on every entryPhase change (each App early
// return renders it in a different place), and a second run would pull the person back into Names
let consumed = false;

/** Pure: where the Names link should land for this permissions payload. */
export function namesLinkTarget(data) {
  if (!data) return null;
  return canAccessPortal(data, 'orders') ? 'orders' : null;
}

export function useNamesLinkOpen() {
  const { data, isLoading } = usePortalPermissions();

  useEffect(() => {
    if (!WANTED || consumed || isLoading || !data) return;
    consumed = true;
    if (!namesLinkTarget(data)) {
      // no Orders access: the chooser stays, and the flag must not open Names on a later visit
      try { window.history.replaceState({}, '', window.location.pathname); } catch (_) { /* cosmetic */ }
      return;
    }
    // the flag is what Orders reads; put it back in case sign-in rewrote the address (same helper as the Home card)
    openNamesAddress();
    const ui = useUiStore.getState();
    ui.setEntryPhase('app');
    ui.setTab('orders');
  }, [data, isLoading]);
}

export default useNamesLinkOpen;
